<template>
  <div class="pcd-ui pcd-home">
    <div class="pcd-home-inner">
      <header class="pcd-header">
        <div class="pcd-brand"><img src="/favicon.png" alt="" width="36" height="36" /><span>Pinecone DAO</span></div>
        <AccountMenu :busy="!!pendingAction" :blocked="isShowDepositCard || isShowWithdrawCard" :admin="isAdmin" />
      </header>
      <DataStatus placement="inline" />
      <p v-if="loaded && walletStore.dataStatus.contracts !== 'ready'" class="pcd-stale" role="status">{{ t(walletStore.dataStatus.contracts === 'loading' ? '正在重新讀取；以下為上次資料' : '資產與收益尚未更新；以下為上次資料') }}</p>
      <HomeAssets :total="walletStore.pcdBalance" :staked="walletStore.balanceOfShares" :available="walletStore.balanceOfAvailable" :price="walletStore.pcdPrice" :pool-total="walletStore.totalSharesForDividends" :loaded="loaded" :connected="!!accountData.address" :status="walletStore.dataStatus.contracts" :can-transact="canTransact" :busy="!!pendingAction" @deposit="openDeposit" @withdraw="openWithdraw" />
      <HomeRewards :claimable="walletStore.dividendOf" :cumulative="walletStore.accumulativeDividendOf" :claimed="withdrawnDividendAmount" :usdt-balance="walletStore.usdtBalance" :pcd-claimable="walletStore.getClaimablePCD" :loaded="loaded" :connected="!!accountData.address" :status="walletStore.dataStatus.contracts" :can-transact="canTransact" :can-claim-usdt="Number(withdrawableDividendAmount) > 0" :busy="!!pendingAction" :busy-action="pendingAction" :refreshing="isRefreshing" @claim-usdt="runAction('claim-usdt', handleClaimDividends)" @claim-pcd="runAction('claim-pcd', handleClaimPCD)" @refresh="handleRefreshDividend" />
      <p v-if="pendingAction" class="pcd-pending" role="status" aria-live="polite"><van-loading size="18" aria-hidden="true" />{{ t("等待錢包確認或鏈上處理，請勿重複提交。") }}</p>
      <p v-if="legacyGateFeedback" class="pcd-pending" role="status">{{ t(legacyGateFeedback) }}</p>
      <HomeClaimHistory :records="pcdDividendRecords" :status="pcdHistoryStatus" :connected="!!accountData.address" @retry="refreshPcdDividendRecords()" />
      <router-link class="pcd-invite" to="/invite"><van-icon name="friends-o" aria-hidden="true" /><span>{{ t("邀請好友") }}</span><van-icon name="arrow" aria-hidden="true" /></router-link>
    </div>

    <van-action-sheet :show="isShowDepositCard" @update:show="isShowDepositCard = $event" teleport="body" :z-index="11000" class="pcd-ui pcd-sheet" :close-on-popstate="true" aria-labelledby="deposit-title" aria-modal="true" @opened="focusDialog" @closed="restoreDialogFocus" @keydown="trapDialogFocus">
      <div class="pcd-dialog">
        <div class="pcd-dialog-heading"><h2 id="deposit-title">{{ t("存入 PCD") }}</h2><button type="button" class="pcd-icon-button" :aria-label="t(&quot;關閉存入&quot;)" @click="isShowDepositCard = false"><van-icon name="cross" aria-hidden="true" /></button></div>
        <p class="pcd-muted">{{ t("質押 PCD，參與分紅") }}</p>
        <dl class="pcd-dialog-balance"><dt>{{ t("可用餘額") }}</dt><dd>{{ t(formatAmount(walletStore.balanceOfAvailable)) }} <span>PCD</span></dd></dl>
        <label for="depositAmount" class="pcd-field-label">{{ t("存入數量") }} <span>PCD</span></label>
        <div class="pcd-input-wrap"><input id="depositAmount" v-model="depositAmount" type="text" inputmode="decimal" autocomplete="off" :disabled="!!pendingAction" /><button type="button" class="pcd-max" :disabled="!!pendingAction || !canTransact" @click="depositAmount = walletStore.balanceOfAvailable">MAX</button></div>
        <p v-if="depositAmount && !isAmountValid(depositAmount, walletStore.balanceOfAvailable) && !(Number(depositAmount) > Number(walletStore.balanceOfAvailable))" class="pcd-form-error" role="status">{{ t("請輸入有效數量，最多 18 位小數") }}</p>
        <p v-if="Number(depositAmount) > Number(walletStore.balanceOfAvailable)" class="pcd-form-error" role="status">{{ t("存入數量超過可用餘額") }}</p>
        <button type="button" class="pcd-button pcd-button-primary pcd-submit" :disabled="!canTransact || !!pendingAction || !isAmountValid(depositAmount, walletStore.balanceOfAvailable)" @click="runAction('deposit', depositTokensBtn)">{{ t(pendingAction === 'deposit' ? '處理中…' : '確認存入 PCD') }}</button>
        <p class="pcd-dialog-note">{{ t("請在錢包確認交易；鏈上操作需支付 POL 網路費。") }}</p>
      </div>
    </van-action-sheet>

    <van-action-sheet :show="isShowWithdrawCard" @update:show="isShowWithdrawCard = $event" teleport="body" :z-index="11000" class="pcd-ui pcd-sheet" :close-on-popstate="true" aria-labelledby="withdraw-title" aria-modal="true" @opened="focusDialog" @closed="restoreDialogFocus" @keydown="trapDialogFocus">
      <div class="pcd-dialog">
        <div class="pcd-dialog-heading"><h2 id="withdraw-title">{{ t("提幣 PCD") }}</h2><button type="button" class="pcd-icon-button" :aria-label="t(&quot;關閉提幣&quot;)" @click="isShowWithdrawCard = false"><van-icon name="cross" aria-hidden="true" /></button></div>
        <p class="pcd-muted">{{ t("解除質押 PCD，恢復為可用餘額") }}</p>
        <dl class="pcd-dialog-balance"><dt>{{ t("已質押") }}</dt><dd>{{ t(formatAmount(walletStore.balanceOfShares)) }} <span>PCD</span></dd></dl>
        <label for="withdrawAmount" class="pcd-field-label">{{ t("提幣數量") }} <span>PCD</span></label>
        <div class="pcd-input-wrap"><input id="withdrawAmount" v-model="withdrawAmount" type="text" inputmode="decimal" autocomplete="off" :disabled="!!pendingAction" /><button type="button" class="pcd-max" :disabled="!!pendingAction || !canTransact" @click="withdrawAmount = walletStore.balanceOfShares">MAX</button></div>
        <p v-if="withdrawAmount && !isAmountValid(withdrawAmount, walletStore.balanceOfShares) && !(Number(withdrawAmount) > Number(walletStore.balanceOfShares))" class="pcd-form-error" role="status">{{ t("請輸入有效數量，最多 18 位小數") }}</p>
        <p v-if="Number(withdrawAmount) > Number(walletStore.balanceOfShares)" class="pcd-form-error" role="status">{{ t("提幣數量超過已質押數量") }}</p>
        <p v-else class="pcd-muted pcd-remaining">{{ t("剩餘質押") }} {{ t(formatAmount(remainingStake)) }} PCD</p>
        <button type="button" class="pcd-button pcd-button-primary pcd-submit" :disabled="!canTransact || !!pendingAction || !isAmountValid(withdrawAmount, walletStore.balanceOfShares)" @click="runAction('withdraw', withdrawTokensBtn)">{{ t(pendingAction === 'withdraw' ? '處理中…' : '確認提幣 PCD') }}</button>
        <p class="pcd-dialog-note">{{ t("解除質押後，相應數量不再參與分紅。") }}</p>
      </div>
    </van-action-sheet>

  </div>
</template>
<script setup>
import { ensureLegacyClear } from '@/legacy/admission';
import { t } from '@/i18n';
import { useAccountOperationLock } from '@/composables/accountOperationLock';
import AccountMenu from '@/components/AccountMenu.vue';
import { ref, onMounted, computed, watch, nextTick } from "vue";
import { useWalletStore } from '@/stores/walletStore';
import { 
  deposit,
  withdraw,
  claimDividends,
  investInPCD,
  getLastClaimTime,
  getClaimWait,
  fetchDividendRecords as fetchPcdDividendRecords,
  claimPCD,
  getClaimablePCD
} from '@/composables/contractPcdService';
import { approveUSDT } from '@/composables/contractUsdtService';
import { useWalletAccount } from '@/composables/useWalletAccount';
import addresses from '@/composables/addresses.js';
import { fetchDividendRecords } from '@/composables/xpctService';
import { defaultSwal, notify } from '@/composables/swalConfig';

import DataStatus from '@/components/DataStatus.vue';
import HomeAssets from '@/components/home/HomeAssets.vue';
import HomeRewards from '@/components/home/HomeRewards.vue';
import HomeClaimHistory from '@/components/home/HomeClaimHistory.vue';
import { formatAmount, isAmountValid } from '@/components/home/format';
import '@/styles/pcd-ui.css';

const walletStore = useWalletStore();
const accountData = useWalletAccount();

// 本頁面局部狀態
const isShowDepositCard = ref(false);
const isShowWithdrawCard = ref(false);
const isShowInvestCard = ref(false);
const showAccount = ref(false);
const usdtAmount = ref(0);
const depositAmount = ref('');
const withdrawAmount = ref('');

const isRefreshing = ref(false);
const handleRefreshDividend = async () => {
  if (isRefreshing.value) return;
  isRefreshing.value = true;
  try {
    console.log('[HomeView] Manual refresh triggered');
    await walletStore.fetchAllData(true);
    console.log('[HomeView] After refresh: dividendOf=', walletStore.dividendOf);
  } catch (e) {
    console.error('[HomeView] Refresh error:', e);
  } finally {
    setTimeout(() => { isRefreshing.value = false; }, 600);
  }
};

const themeVars = ref({
  '--van-circle-color': '#ff0000',
  '--van-circle-layer-color': '#00ff00',
  '--van-circle-text-color': '#ffffff',
  '--van-circle-text-font-weight': 'bold',
  '--van-circle-text-font-size': '20px',
  '--van-circle-text-line-height': '1.5',
});

// 计算格式化余额
const formattedBalance = computed(() => {
  const v = walletStore.pcdBalance ?? 0;
  return v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
});
const formattedBalanceOfShares = computed(() => {
  const v = walletStore.balanceOfShares ?? 0;
  return v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
});
const formattedBalanceOfAvailable = computed(() => {
  const v = walletStore.balanceOfAvailable ?? 0;
  return v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
});
const formattedAccountLong = computed(() => {
  const addr = accountData.value.address;
  if (!addr) return '';
  // 顯示前8位+...+後8位，總共16位字符
  return addr.length > 16 ? addr.slice(0, 8) + '...' + addr.slice(-8) : addr;
});

const withdrawableDividendAmount = computed(() => {
  const val = walletStore.dividendOf;
  console.log('[HomeView] computed withdrawableDividendAmount, raw dividendOf=', val);
  return (Number(val) || 0).toFixed(2);
});
const withdrawnDividendAmount = computed(() => {
  const accumulative = Number(walletStore.accumulativeDividendOf ?? 0);
  const dividend = Number(walletStore.dividendOf ?? 0);
  return (accumulative - dividend).toFixed(2);
});
const usdtBalance = computed(() => (walletStore.usdtBalance ?? 0).toFixed(2));
// const ethBalance = computed(() => walletStore.polBalance ?? 0);
// const totalSharesForDividends = computed(() => walletStore.totalSharesForDividends ?? 0);

const currentRate = computed(() => {
  const shares = Number(walletStore.balanceOfShares) || 0;
  const total = Number(walletStore.totalSharesForDividends) || 1;
  return Math.min(100, (shares / total) * 100);
});
const text = computed(() => `${currentRate.value.toFixed(2)}%`);

// 新增圖表計算
const availablePercent = computed(() => {
  const available = Number(walletStore.balanceOfAvailable) || 0;
  const total = Number(walletStore.pcdBalance) || 1;
  return ((available / total) * 100).toFixed(2);
});

// 計算質押百分比
const stakePercentage = computed(() => {
  const shares = Number(walletStore.balanceOfShares) || 0;
  const total = Number(walletStore.pcdBalance) || 0;
  if (total === 0) return 0;
  return (shares / total) * 100;
});

// 計算可用百分比
const availablePercentage = computed(() => {
  const available = Number(walletStore.balanceOfAvailable) || 0;
  const total = Number(walletStore.pcdBalance) || 0;
  if (total === 0) return 0;
  return (available / total) * 100;
});

// 方法封裝：斷開錢包
const closeWallet = () => {
  showAccount.value = false;
  walletStore.disconnectWallet();
};

// 操作按鈕方法
const isDepositing = ref(false);
const depositTokensBtn = async () => {
  if (!depositAmount.value || Number(depositAmount.value) <= 0) {
    await notify.info('請輸入存入數量', '請輸入要存入的PCD數量');
    return;
  }
  isDepositing.value = true;
  try {
    isShowDepositCard.value = false; // 先關閉彈窗
    setTimeout(async () => {
      await notify.loading('存入進行中', '請在錢包確認，並等待鏈上處理…');
    }, 300);
    await deposit(depositAmount.value);
    await walletStore.fetchAllData(true);
    depositAmount.value = '';
    setTimeout(async () => {
      await notify.success('存入成功', 'PCD已成功存入合約');
      await walletStore.fetchAllData(true);
      if (typeof refreshPcdDividendRecords === 'function') {
        const addr = accountData.value?.address;
        if (addr) {
          await refreshPcdDividendRecords(addr);
        }
      }
    }, 300);
  } catch (error) {
    if (error && (error.code === 4001 || error.message?.toLowerCase().includes('unknown rpc error') || error.message?.includes('User rejected') || error.message?.includes('cancel'))) {
      setTimeout(async () => {
        await notify.cancel('已取消', '用戶已取消操作');
      }, 300);
    } else {
      setTimeout(async () => {
        await notify.error('存入失敗', error?.message || '請檢查錢包或網絡');
      }, 300);
    }
    console.error('存入代币失败:', error);
  } finally {
    isDepositing.value = false;
  }
};

const isWithdrawing = ref(false);
const withdrawTokensBtn = async () => {
  if (!withdrawAmount.value || Number(withdrawAmount.value) <= 0) {
    await notify.info('請輸入提幣數量', '請輸入要提取的PCD數量');
    return;
  }
  isWithdrawing.value = true;
  try {
    isShowWithdrawCard.value = false; // 先關閉彈窗
    // 等待彈窗動畫結束再顯示提示，避免被遮擋
    setTimeout(async () => {
      await notify.loading('提幣進行中', '請在錢包確認，並等待鏈上處理…');
    }, 300);
    await withdraw(withdrawAmount.value);
    await walletStore.fetchAllData(true);
    withdrawAmount.value = '';
    setTimeout(async () => {
      await notify.success('提幣成功', 'PCD已成功提取到您的錢包');
      // 提幣成功後主動刷新全部數據（包括分紅記錄等）
      await walletStore.fetchAllData(true);
      // 強制刷新分紅記錄時，顯式傳入最新的 address
      if (typeof refreshPcdDividendRecords === 'function') {
        const addr = accountData.value?.address;
        if (addr) {
          await refreshPcdDividendRecords(addr);
        }
      }
    }, 300);
  } catch (error) {
    if (error && (error.code === 4001 || error.message?.toLowerCase().includes('unknown rpc error') || error.message?.includes('User rejected') || error.message?.includes('cancel'))) {
      await notify.cancel('已取消', '用戶已取消操作');
    } else {
      await notify.error('提幣失敗', error?.message || '請檢查錢包或網絡');
    }
    console.error('提取代币失败:', error);
  } finally {
    isWithdrawing.value = false;
  }
};

const investInRiceBtn = async () => {
  try {
    isShowInvestCard.value = false;
    await investInPCD(walletStore.currentAccount, usdtAmount.value);
    await walletStore.fetchAllData(true);
    usdtAmount.value = '';
  } catch (error) {
    console.error('投资失败:', error);
  }
};

// 监听钱包地址变化，自动刷新store数据
watch(() => walletStore.currentAccount, async (newVal) => {
  if (newVal) {
    await walletStore.fetchAllData(true);
  }
});

// 监听 usdtAmount 变化，进行授权检查
watch(usdtAmount, async (newVal) => {
  if (newVal) {
    try {
      await approveUSDT(walletStore.currentAccount, newVal);
    } catch (error) {
      console.error('USDT 授权失败:', error);
    }
  }
});

// 領取記錄
const pcdDividendRecords = ref([]);
let historyRequest = 0;
const pcdHistoryStatus = ref('idle');

async function refreshPcdDividendRecords(addr) {
  const address = addr || accountData.value?.address;
  const request = ++historyRequest;
  pcdDividendRecords.value = [];
  if (!address) { pcdHistoryStatus.value = 'idle'; return; }
  pcdHistoryStatus.value = 'loading';
  try {
    const records = await fetchPcdDividendRecords(address, 5);
    if (request !== historyRequest || accountData.value?.address !== address) return;
    pcdDividendRecords.value = records;
    pcdHistoryStatus.value = 'ready';
  } catch (error) {
    if (request === historyRequest && accountData.value?.address === address) pcdHistoryStatus.value = 'error';
  }
}

onMounted(async () => {
  refreshPcdDividendRecords();
  // 確保首頁加載時主動獲取最新數據
  if (accountData.value?.address) {
    console.log('[HomeView] onMounted: triggering fetchAllData');
    await walletStore.fetchAllData(true);
  }
  // nextTick(() => {
  //   const el = document.querySelector('.auto-shrink-font');
  //   if (el) {
  //     el.setAttribute('data-length', String(el.textContent.length));
  //   }
  // });
});

watch(() => accountData.value?.address, (newVal) => {
  refreshPcdDividendRecords(newVal);
});

// 用戶領取分紅
const handleClaimDividends = async () => {
  if (!withdrawableDividendAmount.value || Number(withdrawableDividendAmount.value) <= 0) {
    await notify.info('暫無可領取獎勵', '目前沒有可領取的分紅獎勵');
    return;
  }
  // 檢查冷卻時間
  const addr = accountData.value?.address;
  if (addr) {
    try {
      const [lastClaim, claimWait] = await Promise.all([
        getLastClaimTime(addr),
        getClaimWait()
      ]);
      const now = Math.floor(Date.now() / 1000);
      const nextClaim = Number(lastClaim) + Number(claimWait);
      if (now < nextClaim) {
        const remain = nextClaim - now;
        const min = Math.floor(remain / 60);
        const sec = remain % 60;
        await notify.info('冷卻中', `請等待 ${min > 0 ? min + '分' : ''}${sec}秒後再領取`);
        return;
      }
    } catch (e) {
      // 查詢失敗時允許繼續
      console.warn('查詢冷卻時間失敗', e);
    }
  }
  try {
    await claimDividends();
    await walletStore.fetchAllData(true);
    // 領取成功後主動刷新分紅記錄
    if (typeof refreshPcdDividendRecords === 'function') {
      const addr = accountData.value?.address;
      if (addr) {
        await refreshPcdDividendRecords(addr);
      }
    }
    await notify.success('領取成功', 'USDT分紅已成功領取');
  } catch (e) {
    if (e && (e.code === 4001 || e.message?.toLowerCase().includes('unknown rpc error') || e.message?.includes('User rejected') || e.message?.includes('cancel'))) {
      await notify.cancel('已取消', '用戶已取消操作');
    } else {
      await notify.error('領取失敗', e?.message || '請檢查錢包或網絡');
    }
  }
};

// 新增領取PCD專用方法
const handleClaimPCD = async () => {
  const address = accountData.value?.address;
  if (!address) {
    await notify.info('請先連接錢包', '無法獲取用戶地址');
    return;
  }
  let claimable = 0;
  try {
    claimable = await getClaimablePCD(address);
  } catch (e) {
    await notify.error('查詢可領取PCD失敗', e?.message || '請檢查網絡');
    return;
  }
  if (!claimable || Number(claimable) <= 0) {
    await notify.info('暫無可領取PCD', '目前沒有可領取的PCD');
    return;
  }
  try {
    await claimPCD(address);
    await notify.success('領取成功', 'PCD已成功領取到您的錢包');
    await walletStore.fetchAllData(true);
    await refreshPcdDividendRecords(address);
  } catch (e) {
    if (e && (e.code === 4001 || e.message?.toLowerCase().includes('unknown rpc error') || e.message?.includes('User rejected') || e.message?.includes('cancel'))) {
      await notify.cancel('已取消', '用戶已取消操作');
    } else {
      await notify.error('領取PCD失敗', e?.message || '請檢查錢包或網絡');
    }
  }
};

// 判斷是否在錢包APP環境
// function isWalletApp() {
//   const ua = window.navigator.userAgent.toLowerCase();
//   // 常見錢包APP UA關鍵字，可根據需要擴充
//   return (
//     ua.includes('imtoken') ||
//     ua.includes('metamask') ||
//     ua.includes('bitkeep') ||
//     ua.includes('tokenpocket') ||
//     ua.includes('mathwallet') ||
//     ua.includes('okx')
//   );
// }

const connectWallet = () => {
  walletStore.connectWallet();
};

const isAdmin = computed(() => {
  const current = (accountData.value?.address || '').toLowerCase();
  const admins = (addresses.adminAddress || '').toLowerCase().split(',');
  return !!current && admins.includes(current);
});

const claimablePCD = computed(() => (walletStore.getClaimablePCD ?? 0).toFixed ? walletStore.getClaimablePCD.toFixed(2) : Number(walletStore.getClaimablePCD || 0).toFixed(2));

// 計算預估質押比例
const estimatedStakePercentage = computed(() => {
  const deposit = Number(depositAmount.value) || 0;
  const currentShares = Number(walletStore.balanceOfShares) || 0;
  const totalShares = Number(walletStore.totalSharesForDividends) || 1;
  
  if (deposit <= 0) return '0.00';
  
  const newPercentage = ((currentShares + deposit) / totalShares) * 100;
  return newPercentage.toFixed(2);
});

// 計算剩餘質押數量
const remainingStake = computed(() => {
  const current = Number(walletStore.balanceOfShares) || 0;
  const withdraw = Number(withdrawAmount.value) || 0;
  const remaining = current - withdraw;
  return remaining > 0 ? remaining.toFixed(2) : '0.00';
});

// 計算新的質押比例
const newStakePercentage = computed(() => {
  const withdraw = Number(withdrawAmount.value) || 0;
  const currentShares = Number(walletStore.balanceOfShares) || 0;
  const totalShares = Number(walletStore.totalSharesForDividends) || 1;
  
  if (withdraw <= 0) return currentRate.value.toFixed(2);
  
  const newShares = currentShares - withdraw;
  const newPercentage = newShares > 0 ? (newShares / totalShares) * 100 : 0;
  return newPercentage.toFixed(2);
});

// const balanceAvailableEl = ref(null);
// function adjustFontSize() {
//   const el = balanceAvailableEl.value;
//   if (!el) return;
//   el.style.fontSize = '22px'; // 預設最大字號
//   el.style.whiteSpace = 'nowrap';
//   el.style.overflow = 'visible';
//   el.style.textOverflow = 'clip';
//   let parentWidth = el.parentNode.offsetWidth;
//   let fontSize = 22;
//   while (el.scrollWidth > parentWidth && fontSize > 12) {
//     fontSize -= 1;
//     el.style.fontSize = fontSize + 'px';
//   }
// }
// watch(() => formattedBalanceOfAvailable.value, () => {
//   nextTick(adjustFontSize);
// });
// onMounted(() => {
//   nextTick(adjustFontSize);
//   window.addEventListener('resize', adjustFontSize);
// });

// UI guards only: existing handlers below retain their contract calls and checks.
const pendingAction = ref(''), legacyGateFeedback = ref('');
useAccountOperationLock(computed(() => !!pendingAction.value));
const loaded = computed(() => !!accountData.value.address
  && walletStore.dataAddress === accountData.value.address.toLowerCase()
  && walletStore.hasContractData);
const canTransact = computed(() => loaded.value && accountData.value.chainId === 137
  && walletStore.dataStatus.contracts === 'ready' && !walletStore.connectionError);
async function runAction(name, handler) {
  if (pendingAction.value || !canTransact.value) return;
  pendingAction.value = name;
  try {
    legacyGateFeedback.value = '';
    try { await ensureLegacyClear(); } catch { legacyGateFeedback.value = '舊版退出交易待核實，請先到歷史權益查詢。'; return; }
    await handler();
  }
  finally { pendingAction.value = ''; }
}
let dialogTrigger;
function openDialog(kind) {
  if (!canTransact.value || pendingAction.value) return;
  dialogTrigger = document.activeElement;
  if (kind === 'deposit') isShowDepositCard.value = true;
  if (kind === 'withdraw') isShowWithdrawCard.value = true;
}
const openDeposit = () => openDialog('deposit');
const openWithdraw = () => openDialog('withdraw');
function focusDialog() {
  const dialogs = [...document.querySelectorAll('.pcd-sheet[role="dialog"]')];
  const dialog = dialogs.find(el => el.style.display !== 'none');
  dialog?.querySelector('button:not(:disabled)')?.focus();
}
function restoreDialogFocus() {
  nextTick(() => { if (dialogTrigger?.isConnected) dialogTrigger.focus(); });
}
function trapDialogFocus(event) {
  if (event.key === 'Escape') {
    isShowDepositCard.value = false;
    isShowWithdrawCard.value = false;
    return;
  }
  if (event.key !== 'Tab') return;
  const controls = [...event.currentTarget.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href]')];
  const first = controls[0], last = controls[controls.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
}
// Closing stale dialogs also prevents a retained amount from being submitted for a new account.
watch(() => accountData.value.address, () => {
  isShowDepositCard.value = false;
  isShowWithdrawCard.value = false;
  depositAmount.value = '';
  withdrawAmount.value = '';
});
</script>
